/**
 * Command bar (design council Part IV — Layout Law).
 *
 * One docked, in-flow top strip (40px) that spans the MAP REGION only (between
 * the left rail and the right detail panel). It replaces every former floating
 * top cluster — the SITREP toolbar, the ASSIST toolbar, the Draw-Sector pill,
 * the bottom-centre Declare-Community pill, and the corner mode switch — so no
 * chrome floats over the map centre and the QA-5 / F-1 overlap class is extinct.
 *
 * Frequent-action groups only (council Part IX clutter test — the command bar
 * holds Report / Request / Author; low-frequency switches live in
 * settings/overflow, not on the bar):
 *   [ REPORT ]  SITREP scope + Generate (one-click) + Recent▾   (SitrepControls)
 *   [ REQUEST ] New request                                     (AssistanceControls)
 *   [ AUTHOR ]  Draw▾ split-button → Draw Sector (gated on an
 *               incident) + Declare Community
 *   ─────────────────────────────────────────────────────────── (spacer)
 *   [ ⚙ ]       Panel (#197) + Settings▾ overflow → the low-frequency
 *               Operator / Briefing profile switch (#191) + Shift Log (#381, far right)
 *
 * The assist triage queue is NOT on the bar — it lifted to the LeftRail
 * Coordination → Assist worklist (#378); the command bar keeps only "New request".
 *
 * The Operator/Briefing switch is a low-frequency view-profile toggle, so it
 * lives in the Settings▾ overflow menu rather than on the bar itself (#191).
 *
 * While a map tool is active (sector draw / community placement) the AUTHOR
 * button flips to a Cancel control; the step-by-step hint lives in the transient
 * bottom tool-status strip (rendered by App), not here.
 */
import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  SEAT_PROFILE_SEEDS,
  seedToProfileView,
  type SavedSeatProfile,
  type SitrepReport,
} from '@tsr/shared-types';
import { SitrepControls, type SitrepIncidentSelection } from './SitrepControls';
import { AssistanceControls } from './AssistanceControls';
import { NewsMenu } from './NewsMenu';
import { DroneMenu } from './DroneMenu';
import { SEAT_NAME_MAX, useSeatName } from './lib/seatIdentity';
import {
  isCahsMonochromeMode,
  renderedCahsMonochromeMode,
  setCahsMonochromeMode,
} from './cahsRenderMode';
import type { BboxQuery } from './api';
import type { SavedRegion } from './savedViews';
import { DEFAULT_VIEW_PROFILE, type SeatProfileTarget } from './seatProfiles';
import { AuthContext } from './auth/AuthContext';
import { IDENTITY_SEPARATOR, SIGNED_IN_AS, roleDisplayLabel } from './authVocabulary';
import { useMediaQuery } from './lib/useMediaQuery';
import { WIDE_DISPLAY_QUERY } from './lib/wideDisplay';
import { useElementSize } from './lib/useElementSize';
import {
  commandBarTierForWidth,
  compactCancelLabel,
  compactSearch,
  demoteMediaMenus,
  demoteViewGroup,
  showBarCaptions,
} from './lib/commandBarLayout';
import { CommandBarTierContext, useCommandBarTier } from './lib/commandBarTier';

export interface CommandBarProps {
  /** Leading SEARCH group (#139/#141 unified search — folded in from top-right). */
  search?: ReactNode;
  /** VIEW group (#149) — fly to the saved home view + set the current view as home. */
  onFlyHome: () => void;
  /** VIEW group (Phase 6) — National view: ALWAYS fly to all of Canada, ignoring
   *  any saved home (the deliberate counterpart to Home). Bound to ⇧H. */
  onFlyNational: () => void;
  onSetHome: () => void;
  /** Brief "saved ✓" confirmation flash after onSetHome. */
  homeJustSet: boolean;
  /** VIEW group (#154) — operator-saved named regions: fly to one, save the
   *  current extent as a new region, delete one. */
  savedRegions: SavedRegion[];
  onFlyRegion: (region: SavedRegion) => void;
  onSaveRegion: (label: string) => void;
  onDeleteRegion: (region: SavedRegion) => void;
  /** REPORT group. */
  currentBbox: BboxQuery | null;
  selectedIncident: SitrepIncidentSelection | null;
  onOpenSitrep: (sitrep: SitrepReport) => void;
  /** AUTHOR group. */
  incidentSelected: boolean;
  drawingSector: boolean;
  placingCommunity: boolean;
  onDrawSector: () => void;
  onDeclareCommunity: () => void;
  /** MODE group. */
  briefingMode: boolean;
  onSetBriefingMode: (briefing: boolean) => void;
  /** Open the operations panel (#197) in a second window for a dual-monitor seat. */
  onOpenPanel: () => void;
  /** SETTINGS → Shift Log (#381): open the hand-over briefing drawer. Relocated off
   *  the left rail to the Settings ▾ menu (session-context, low-frequency). */
  onOpenShiftLog: () => void;
  /** SETTINGS → Seat profile (#187): the operator's custom saved profiles (the
   *  curated seeds are code constants rendered by the menu itself), plus
   *  apply / save-current / delete. */
  seatProfiles: SavedSeatProfile[];
  onApplySeatProfile: (target: SeatProfileTarget) => void;
  onSaveSeatProfile: (label: string) => void;
  onDeleteSeatProfile: (profile: SavedSeatProfile) => void;
}

/**
 * IDENTITY CHIP (RBAC Phase 1, #566) — the persistent per-seat vantage marker in the
 * command bar: "Signed in as <name> · <Agency> · <Role>". Renders ONLY for a signed-in
 * operator (the demo / anonymous COP shows nothing). The full string is the title + aria
 * label (hover / screen reader); the visible text truncates so a long agency name (e.g.
 * the PEOC) never overflows the bar, and at the tightest tier it collapses to Agency ·
 * Role (the per-seat vantage). Reads AuthContext defensively so it renders as "not signed
 * in" outside an <AuthProvider> (tests). Role labels come from authVocabulary (verbatim
 * Ontario IMS, ruling R5); the agency name is DB-driven.
 */
function IdentityChip(): JSX.Element | null {
  const authUser = useContext(AuthContext)?.user ?? null;
  const tier = useCommandBarTier();
  if (!authUser) return null;
  const roleLabel = roleDisplayLabel(authUser.role);
  const agencyName = authUser.agency?.name ?? null;
  const sep = ` ${IDENTITY_SEPARATOR} `;
  const full = [`${SIGNED_IN_AS} ${authUser.displayName}`, agencyName, roleLabel]
    .filter(Boolean)
    .join(sep);
  // Tightest tier: collapse to Agency · Role (or just Role) to spare bar width.
  const visible =
    tier === 'minimal' ? [agencyName, roleLabel].filter(Boolean).join(sep) : full;
  return (
    <div
      data-testid="identity-chip"
      title={full}
      aria-label={full}
      className="flex h-7 min-w-0 items-center rounded border border-slate-600 bg-slate-800 px-2 text-[11px] text-slate-300"
    >
      <span className="max-w-[15rem] truncate">{visible}</span>
    </div>
  );
}

export function CommandBar({
  search,
  onFlyHome,
  onFlyNational,
  onSetHome,
  homeJustSet,
  savedRegions,
  onFlyRegion,
  onSaveRegion,
  onDeleteRegion,
  currentBbox,
  selectedIncident,
  onOpenSitrep,
  incidentSelected,
  drawingSector,
  placingCommunity,
  onDrawSector,
  onDeclareCommunity,
  briefingMode,
  onSetBriefingMode,
  onOpenPanel,
  onOpenShiftLog,
  seatProfiles,
  onApplySeatProfile,
  onSaveSeatProfile,
  onDeleteSeatProfile,
}: CommandBarProps): JSX.Element {
  const toolActive = drawingSector || placingCommunity;
  // Genuinely-wide enhancement (#131, re-tuned standard-first by #192): the
  // left-anchor only engages when the display is genuinely wide (width > 2560
  // AND aspect ≥ 2:1 — see wideDisplay.ts). Standard monitors, including a 27"
  // QHD at exactly 2560px, keep the canonical layout.
  const wideDisplay = useMediaQuery(WIDE_DISPLAY_QUERY);
  // Density tier from the bar's MEASURED width (= the map region, council
  // Part IV — never the viewport: rail + panel can leave a 1920 desk only
  // ~1180px). Low-frequency groups demote into Settings ▾ as the region
  // narrows (Part IX, the #191 pattern); see lib/commandBarLayout.ts.
  const [barRef, barSize] = useElementSize<HTMLDivElement>();
  const tier = commandBarTierForWidth(barSize.width);
  const captions = showBarCaptions(tier);

  // DEV tripwire — the dynamic half of the overflow regression guard: if the
  // bar's content ever exceeds its region again (a new group lands without
  // re-tuning the tier thresholds), every browser-verify ritual fails its
  // "zero console errors" gate instead of the bar silently overlapping the
  // detail panel. Skips the unmeasured first frame (width 0 renders the full
  // tier by design until the ResizeObserver reports) and logs once per
  // offending tier, not per render.
  const trippedTierRef = useRef<string | null>(null);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (barSize.width <= 0) return;
    const el = barRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth + 1) {
      if (trippedTierRef.current !== tier) {
        trippedTierRef.current = tier;
        console.error(
          `[CommandBar] content overflows the map region (${el.scrollWidth}px > ${el.clientWidth}px) ` +
            `at tier "${tier}" — re-measure and re-tune lib/commandBarLayout.ts (council Part IV: no chrome overlap)`,
        );
      }
    } else {
      trippedTierRef.current = null;
    }
  });

  return (
    <CommandBarTierContext.Provider value={tier}>
    <div
      ref={barRef}
      data-testid="command-bar"
      data-bar-tier={tier}
      className={`relative z-30 flex h-[var(--command-bar-h)] shrink-0 items-center border-b border-slate-700 bg-slate-900/95 px-2 text-slate-100 ${
        tier === 'minimal' ? 'gap-1.5' : 'gap-2'
      }`}
    >
      {!demoteViewGroup(tier) && (
        <>
          <HomeViewGroup
            onFlyHome={onFlyHome}
            onFlyNational={onFlyNational}
            onSetHome={onSetHome}
            homeJustSet={homeJustSet}
            savedRegions={savedRegions}
            onFlyRegion={onFlyRegion}
            onSaveRegion={onSaveRegion}
            onDeleteRegion={onDeleteRegion}
            canSaveRegion={!!currentBbox}
          />
          <Divider />
        </>
      )}
      {search && (
        <>
          {/* SEARCH is the one flexible element — it absorbs slack on wide
              screens (capped) and shrinks on narrow ones, so the fixed control
              groups never compress or wrap. On a genuinely wide display
              (#192 detection, council Part V) it stops absorbing slack so every
              group LEFT-ANCHORS in the map region (the eye-travel fix) and the
              slack pools harmlessly on the right. At the minimal tier it
              collapses to a magnifier button whose popover hosts the SAME
              SearchBox (Cmd-K still opens it). */}
          {compactSearch(tier) ? (
            <CompactSearch search={search} />
          ) : (
            <div
              className={
                wideDisplay
                  ? 'w-[16rem] min-w-[7rem] flex-none'
                  : 'min-w-[7rem] max-w-[12rem] flex-1 xl:max-w-[16rem]'
              }
            >
              {search}
            </div>
          )}
          <Divider />
        </>
      )}
      <SitrepControls
        currentBbox={currentBbox}
        selectedIncident={selectedIncident}
        savedRegions={savedRegions}
        onOpen={onOpenSitrep}
      />
      <Divider />
      <AssistanceControls selectedIncident={selectedIncident} />
      <Divider />
      {!demoteMediaMenus(tier) && (
        <>
          <NewsMenu />
          <Divider />
          <DroneMenu />
          <Divider />
        </>
      )}
      <AuthorGroup
        incidentSelected={incidentSelected}
        toolActive={toolActive}
        drawingSector={drawingSector}
        onDrawSector={onDrawSector}
        onDeclareCommunity={onDeclareCommunity}
      />

      {/* TRAILING — far right, joins the bar (council Part IV). On a genuinely
          wide display (#192 detection) it drops ml-auto so it left-anchors with
          the rest of the groups (council Part V). Holds only the Panel opener +
          the Settings▾ overflow; the Operator/Briefing switch moved into
          Settings (#191). */}
      <div
        data-testid="command-bar-trailing"
        className={`flex shrink-0 items-center gap-2 pl-1 ${wideDisplay ? 'ml-0' : 'ml-auto'}`}
      >
        {/* Persistent per-seat identity chip (RBAC Phase 1, #566) — renders only when
            signed in; the demo / anonymous COP shows nothing. */}
        <IdentityChip />
        {/* Operations panel (#197) — opens the second-monitor surface. */}
        <button
          type="button"
          data-testid="open-panel-btn"
          onClick={onOpenPanel}
          title="Open the operations panel in a second window (dual-monitor seat)"
          aria-label="Open operations panel"
          className="flex h-7 items-center gap-1 rounded border border-slate-600 bg-slate-800 px-1.5 text-[11px] text-slate-300 hover:bg-slate-700"
        >
          {/* two-window glyph */}
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="13" height="10" rx="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 18h13V9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {captions && <span>Panel</span>}
        </button>
        <Divider />
        <SettingsMenu
          briefingMode={briefingMode}
          onSetBriefingMode={onSetBriefingMode}
          onOpenShiftLog={onOpenShiftLog}
          seatProfiles={seatProfiles}
          onApplySeatProfile={onApplySeatProfile}
          onSaveSeatProfile={onSaveSeatProfile}
          onDeleteSeatProfile={onDeleteSeatProfile}
          demotedMedia={demoteMediaMenus(tier)}
          demotedView={
            demoteViewGroup(tier)
              ? {
                  onFlyHome,
                  onFlyNational,
                  onSetHome,
                  homeJustSet,
                  savedRegions,
                  onFlyRegion,
                  onSaveRegion,
                  onDeleteRegion,
                  canSaveRegion: !!currentBbox,
                }
              : null
          }
        />
      </div>
    </div>
    </CommandBarTierContext.Provider>
  );
}

/**
 * Minimal-tier search (the last rung of the density ladder): a magnifier
 * button whose popover hosts the SAME SearchBox node the bar normally inlines.
 * Cmd/Ctrl-K opens the popover too (SearchBox's own Cmd-K handler only exists
 * while mounted), and the input is focused once revealed.
 */
function CompactSearch({ search }: { search: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    // Focus the hosted SearchBox input once the popover mounts.
    wrapRef.current?.querySelector('input')?.focus();
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative flex shrink-0 items-center" data-testid="compact-search">
      <button
        type="button"
        data-testid="compact-search-btn"
        onClick={() => setOpen((v) => !v)}
        title="Search places & entities (⌘K)"
        aria-label="Search"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-7 w-7 items-center justify-center rounded border border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700"
      >
        {/* magnifier glyph */}
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m20 20-3.8-3.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          data-testid="compact-search-popover"
          className="absolute left-0 top-full z-50 mt-1 w-80 rounded-lg border border-slate-700 bg-slate-900/95 p-1.5 shadow-lg"
        >
          {search}
        </div>
      )}
    </div>
  );
}

/** The VIEW group's handlers, threaded into Settings ▾ when the group demotes
 *  off the bar at the minimal tier. */
interface DemotedViewProps {
  onFlyHome: () => void;
  onFlyNational: () => void;
  onSetHome: () => void;
  homeJustSet: boolean;
  savedRegions: SavedRegion[];
  onFlyRegion: (region: SavedRegion) => void;
  onSaveRegion: (label: string) => void;
  onDeleteRegion: (region: SavedRegion) => void;
  canSaveRegion: boolean;
}

/**
 * Settings ▾ overflow (council Part IX, #191). The home for low-frequency
 * switches that do not earn a persistent slot on the command bar — the
 * Operator / Briefing view-profile toggle (a minister briefing is mostly reached
 * via the §4.26 tokenized URL, not by the operator toggling their own screen)
 * and the #187 seat-profile picker (an operator changes seats at shift
 * boundaries, not minute-to-minute). Mirrors the AUTHOR / Regions menus'
 * outside-click + Escape dismissal.
 *
 * On narrow map regions it is also the demotion target of the overflow ladder
 * (commandBarLayout.ts): News + Drone at the overflow tier, the VIEW group at
 * minimal — the same Part IX move that put Mode and Seat profile here.
 */
function SettingsMenu({
  briefingMode,
  onSetBriefingMode,
  onOpenShiftLog,
  seatProfiles,
  onApplySeatProfile,
  onSaveSeatProfile,
  onDeleteSeatProfile,
  demotedMedia = false,
  demotedView = null,
}: {
  briefingMode: boolean;
  onSetBriefingMode: (briefing: boolean) => void;
  onOpenShiftLog: () => void;
  seatProfiles: SavedSeatProfile[];
  onApplySeatProfile: (target: SeatProfileTarget) => void;
  onSaveSeatProfile: (label: string) => void;
  onDeleteSeatProfile: (profile: SavedSeatProfile) => void;
  demotedMedia?: boolean;
  demotedView?: DemotedViewProps | null;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [profileLabel, setProfileLabel] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const captions = showBarCaptions(useCommandBarTier());

  // #264 — the desk's seat name (claim identity; lib/seatIdentity). The draft
  // re-syncs from the persisted value whenever the menu opens.
  const [seat, setSeat] = useSeatName();
  const [seatDraft, setSeatDraft] = useState('');
  useEffect(() => {
    if (open) setSeatDraft(seat ?? '');
  }, [open, seat]);
  // RBAC R.2b (#314) — when signed in, identity is the authenticated user, not the
  // honor-system seat. Read the context defensively (this menu also renders outside
  // an <AuthProvider> in tests) so an absent provider behaves as "not signed in".
  const authCtx = useContext(AuthContext);
  const authUser = authCtx?.user ?? null;
  // RBAC R.2c (#314) — the Sign-out action, shown only when signed in.
  const authLogout = authCtx?.logout ?? null;

  const submitSaveProfile = (): void => {
    const trimmed = profileLabel.trim();
    if (!trimmed) return;
    onSaveSeatProfile(trimmed);
    setProfileLabel('');
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative flex shrink-0 items-center" data-testid="settings-controls">
      <button
        type="button"
        data-testid="settings-menu-btn"
        onClick={() => setOpen((v) => !v)}
        title="Settings"
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-7 items-center gap-1 rounded border border-slate-600 bg-slate-800 px-1.5 text-[11px] text-slate-300 hover:bg-slate-700"
      >
        {/* gear glyph */}
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {captions && <span>Settings</span>}
        <span aria-hidden>▾</span>
      </button>
      {open && (
        <div
          data-testid="settings-menu"
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-slate-700 bg-slate-900/95 p-2 shadow-lg"
        >
          {/* VIEW — demoted off the bar at the minimal tier only. */}
          {demotedView && (
            <div className="mb-2 space-y-0.5 border-b border-slate-700 pb-2" data-testid="settings-view-section">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                View
              </span>
              <button
                type="button"
                data-testid="settings-fly-home-btn"
                onClick={() => {
                  setOpen(false);
                  demotedView.onFlyHome();
                }}
                className="w-full rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
              >
                Fly to home view (H)
              </button>
              <button
                type="button"
                data-testid="settings-set-home-btn"
                onClick={() => demotedView.onSetHome()}
                className={`w-full rounded px-2 py-1.5 text-left text-xs hover:bg-slate-800 ${
                  demotedView.homeJustSet ? 'text-emerald-300' : 'text-slate-200'
                }`}
              >
                {demotedView.homeJustSet ? '✓ Home view saved' : 'Set current view as home'}
              </button>
              <button
                type="button"
                data-testid="settings-fly-national-btn"
                onClick={() => {
                  setOpen(false);
                  demotedView.onFlyNational();
                }}
                className="w-full rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
              >
                National view — all of Canada (⇧H)
              </button>
              <RegionsMenu
                savedRegions={demotedView.savedRegions}
                onFlyRegion={demotedView.onFlyRegion}
                onSaveRegion={demotedView.onSaveRegion}
                onDeleteRegion={demotedView.onDeleteRegion}
                canSaveRegion={demotedView.canSaveRegion}
                variant="row"
              />
            </div>
          )}

          {/* NEWS + DRONE — demoted off the bar at the overflow tier (Part IX:
              ambient media pins are low-frequency; the bar keeps Report /
              Request / Author). */}
          {demotedMedia && (
            <div className="mb-2 space-y-0.5 border-b border-slate-700 pb-2" data-testid="settings-media-section">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Media
              </span>
              <NewsMenu variant="row" />
              <DroneMenu variant="row" />
            </div>
          )}

          {/* SESSION — Shift Log (#381). Relocated off the left rail: it's a
              hand-over briefing read at shift boundaries (session-context,
              low-frequency), so per council Part IX it lives in the overflow menu,
              not as a primary rail tab. Opens the briefing as a drawer. */}
          <div data-testid="settings-session-section" className="mb-2 space-y-0.5 border-b border-slate-700 pb-2">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Session
            </span>
            <button
              type="button"
              data-testid="settings-shift-log-btn"
              onClick={() => {
                setOpen(false);
                onOpenShiftLog();
              }}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
            >
              <span aria-hidden>🕑</span>
              <span className="flex-1">Shift Log</span>
              <span aria-hidden className="text-slate-500">
                ▸
              </span>
            </button>
          </div>

          {/* MODE — the relocated Operator/Briefing view-profile switch (#191). */}
          <div data-testid="mode-switch" className="space-y-1.5">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Mode
            </span>
            <div className="flex overflow-hidden rounded-md border border-slate-700 text-[11px]">
              <button
                type="button"
                data-testid="mode-operator-btn"
                onClick={() => onSetBriefingMode(false)}
                aria-pressed={!briefingMode}
                className={`flex-1 px-2.5 py-1 ${!briefingMode ? 'bg-slate-700 text-slate-100' : 'text-slate-400 hover:bg-slate-800'}`}
              >
                Operator
              </button>
              <button
                type="button"
                data-testid="mode-briefing-btn"
                onClick={() => onSetBriefingMode(true)}
                aria-pressed={briefingMode}
                className={`flex-1 px-2.5 py-1 ${briefingMode ? 'bg-slate-700 text-slate-100' : 'text-slate-400 hover:bg-slate-800'}`}
              >
                Briefing
              </button>
            </div>
            <p className="text-[10px] leading-snug text-slate-500">
              Briefing mode is a simplified read-only view for stakeholders.
            </p>
          </div>

          {/* SYMBOLOGY → Government-compliance symbology (#308) — the in-UI control
              for the shipped CAHS monochrome render seam (./cahsRenderMode). A
              low-frequency display preference, so it lives here per council Part IX.
              (Heading "Symbology", not "Display", to avoid colliding with the
              adjacent "Display name" section header.) */}
          <GovComplianceToggle />

          {/* DISPLAY NAME (#264 → RBAC R.2b #314) — the identity an acknowledged
              alert is claimed under. When signed in this is the authenticated
              user's display name (read-only); when not (demo / enforcement OFF) it
              falls back to the editable desk seat label. Low-frequency, so it lives
              here per council Part IX. Distinct from "Seat profile" (layer presets). */}
          <div className="mt-2 space-y-1.5 border-t border-slate-700 pt-2" data-testid="seat-name-section">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Display name
            </span>
            {authUser ? (
              <div className="space-y-1.5">
                <div data-testid="settings-display-name" className="text-xs text-slate-100">
                  {authUser.displayName}
                </div>
                {/* RBAC Phase 1 (#566) — the acting seat's AGENCY (tenant). Display-only;
                    DB-driven name (e.g. the PEOC / City of Barrie). */}
                {authUser.agency?.name ? (
                  <div data-testid="settings-agency" className="text-[11px] text-slate-300">
                    {authUser.agency.name}
                  </div>
                ) : null}
                {/* RBAC R.3a (#315) — the signed-in IMS role, as its verbatim Ontario IMS
                    display label (ruling R5, authVocabulary). Display-only; the server is
                    the authorization gate (a viewer is 403'd on operator-action writes when
                    AUTH_ENFORCED is on). */}
                <div
                  data-testid="settings-role"
                  className="text-[10px] font-medium uppercase tracking-wider text-slate-400"
                >
                  {roleDisplayLabel(authUser.role)}
                </div>
                {/* RBAC R.2c (#314) — Sign out. Revokes the session + clears identity.
                    When enforcement is ON the root gate then returns to the login
                    screen; when OFF the COP stays up as the anonymous/demo identity. */}
                <button
                  type="button"
                  data-testid="settings-sign-out-btn"
                  onClick={() => {
                    setOpen(false);
                    void authLogout?.();
                  }}
                  className="w-full rounded border border-slate-600 bg-slate-800 px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-700"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <input
                  data-testid="settings-seat-name-input"
                  value={seatDraft}
                  maxLength={SEAT_NAME_MAX}
                  onChange={(e) => setSeatDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSeat(seatDraft);
                  }}
                  placeholder="e.g. Desk 2 / Duty Officer"
                  className="h-7 min-w-0 flex-1 rounded border border-slate-600 bg-slate-900 px-2 text-xs text-slate-100 outline-none focus:border-sky-500"
                />
                <button
                  type="button"
                  data-testid="settings-seat-name-save"
                  onClick={() => setSeat(seatDraft)}
                  className="h-7 shrink-0 rounded bg-sky-600/70 px-2 text-xs text-white hover:bg-sky-600"
                >
                  Save
                </button>
              </div>
            )}
            <p className="text-[10px] leading-snug text-slate-500">
              An acknowledged alert is claimed under this name.
            </p>
          </div>

          {/* SEAT PROFILE (#187) — apply a curated IMS-seat preset (layer set +
              panel arrangement) or one the operator saved. Low-frequency (a seat
              changes at shift boundaries), so it lives here per council Part IX,
              never on the bar. */}
          <div className="mt-2 space-y-1.5 border-t border-slate-700 pt-2" data-testid="seat-profile-section">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Seat profile
            </span>
            <ul className="max-h-56 space-y-0.5 overflow-y-auto">
              <li>
                <button
                  type="button"
                  data-testid="seat-profile-default-btn"
                  onClick={() => {
                    setOpen(false);
                    onApplySeatProfile({
                      id: DEFAULT_VIEW_PROFILE.id,
                      label: DEFAULT_VIEW_PROFILE.label,
                      view: { layerDeltas: {} },
                      isSeed: true,
                    });
                  }}
                  title={DEFAULT_VIEW_PROFILE.description}
                  className="w-full truncate rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
                >
                  {DEFAULT_VIEW_PROFILE.label}
                </button>
              </li>
              {SEAT_PROFILE_SEEDS.map((seed) => (
                <li key={seed.id}>
                  <button
                    type="button"
                    data-testid="seat-profile-seed-btn"
                    onClick={() => {
                      setOpen(false);
                      onApplySeatProfile({
                        id: seed.id,
                        label: seed.label,
                        view: seedToProfileView(seed),
                        isSeed: true,
                      });
                    }}
                    title={seed.description}
                    className="w-full truncate rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
                  >
                    {seed.label}
                  </button>
                </li>
              ))}
              {seatProfiles.map((profile) => (
                <li key={profile.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    data-testid="seat-profile-apply-btn"
                    onClick={() => {
                      setOpen(false);
                      onApplySeatProfile({
                        id: profile.id,
                        label: profile.label,
                        view: profile.view,
                        isSeed: false,
                      });
                    }}
                    title={`Apply ${profile.label}`}
                    className="flex-1 truncate rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
                  >
                    {profile.label}
                  </button>
                  <button
                    type="button"
                    data-testid="seat-profile-delete-btn"
                    onClick={() => onDeleteSeatProfile(profile)}
                    aria-label={`Delete profile ${profile.label}`}
                    title={`Delete ${profile.label}`}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-800 hover:text-rose-300"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-1">
              <input
                data-testid="seat-profile-name-input"
                value={profileLabel}
                onChange={(e) => setProfileLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSaveProfile();
                }}
                maxLength={80}
                placeholder="Save current as profile…"
                className="min-w-0 flex-1 rounded border border-slate-600 bg-slate-800 px-1.5 py-1 text-xs text-slate-100 placeholder:text-slate-500"
              />
              <button
                type="button"
                data-testid="seat-profile-save-btn"
                onClick={submitSaveProfile}
                disabled={!profileLabel.trim()}
                title="Save the current layer set + pinned feeds as a named seat profile"
                className="shrink-0 rounded border border-emerald-500 bg-emerald-600/90 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Save
              </button>
            </div>
            <p className="text-[10px] leading-snug text-slate-500">
              A profile applies its layer set over the default view; saved profiles
              also restore pinned feeds.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SYMBOLOGY → Government-compliance symbology (#308). Exposes the shipped CAHS
 * monochrome render seam ({@link ./cahsRenderMode}) as a sticky per-browser
 * toggle. Self-contained: the glyph render path reads the seam DIRECTLY at
 * build time ({@link ./iconRegistry} `cahs:` → `resolveCahsInk`), not via React
 * props, so nothing threads through the COP tree — this component just reflects
 * the resolved mode and persists the flip.
 *
 * RELOAD-APPLIED, not reactive (deliberate, mirrors the other init-only render
 * seams renderResolution / demoMode). The retint resolves at glyph-BUILD time and
 * the iconRegistry PNG cache keys on colour, not the mono flag — so a live flip
 * would need a cache-bust + a forced rebuild of every CAHS-glyph entity across
 * the COP, a cross-module rewrite out of scope here. Instead we persist the
 * choice immediately and surface a clear "Reload to apply" affordance whenever
 * the pending choice differs from what the current load rendered against.
 *
 * The baseline is {@link renderedCahsMonochromeMode} — captured ONCE at module
 * load, NOT at mount. This component lives inside the Settings menu's `{open &&}`
 * block, so it remounts on every menu-open; comparing against a per-mount
 * snapshot would clear the "Reload to apply" cue the instant the menu reopened,
 * even though the COP is still rendering the pre-flip glyphs.
 */
function GovComplianceToggle(): JSX.Element {
  const [mono, setMono] = useState<boolean>(() => isCahsMonochromeMode());

  const toggle = (next: boolean): void => {
    setMono(next);
    setCahsMonochromeMode(next);
  };
  // Compare against the LOAD-time baseline (survives menu remounts), not a fresh
  // read — the COP only picks up the new mode on an actual page reload.
  const needsReload = mono !== renderedCahsMonochromeMode();

  return (
    <div
      className="mt-2 space-y-1.5 border-t border-slate-700 pt-2"
      data-testid="symbology-section"
    >
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Symbology
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={mono}
        data-testid="cahs-mono-toggle"
        onClick={() => toggle(!mono)}
        className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
      >
        <span>Government-compliance symbology</span>
        <span
          aria-hidden
          className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${
            mono ? 'bg-sky-600' : 'bg-slate-600'
          }`}
        >
          <span
            className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${
              mono ? 'translate-x-3.5' : 'translate-x-0.5'
            }`}
          />
        </span>
      </button>
      <p className="text-[10px] leading-snug text-slate-500">
        Renders hazard glyphs in neutral monochrome ink (urgency stays legible via
        size, ring, motion &amp; label).
        {needsReload && (
          <>
            {' '}
            <button
              type="button"
              data-testid="cahs-mono-reload-btn"
              onClick={() => window.location.reload()}
              className="font-medium text-sky-300 underline hover:text-sky-200"
            >
              Reload to apply
            </button>
          </>
        )}
      </p>
    </div>
  );
}

function Divider(): JSX.Element {
  return <span aria-hidden className="h-5 w-px shrink-0 bg-slate-700" />;
}

/**
 * VIEW (#149 + #154) — a home button that flies to the operator's saved view (or
 * the all-Canada default when unset, #138), a "set current view as home"
 * affordance, and a Regions ▾ menu of operator-saved named region bboxes (#154):
 * fly to one, delete one, or save the current map extent as a new region. All
 * buttons are shrink-0 so they never compress the search box.
 */
function HomeViewGroup({
  onFlyHome,
  onFlyNational,
  onSetHome,
  homeJustSet,
  savedRegions,
  onFlyRegion,
  onSaveRegion,
  onDeleteRegion,
  canSaveRegion,
}: {
  onFlyHome: () => void;
  onFlyNational: () => void;
  onSetHome: () => void;
  homeJustSet: boolean;
  savedRegions: SavedRegion[];
  onFlyRegion: (region: SavedRegion) => void;
  onSaveRegion: (label: string) => void;
  onDeleteRegion: (region: SavedRegion) => void;
  canSaveRegion: boolean;
}): JSX.Element {
  const captions = showBarCaptions(useCommandBarTier());
  return (
    <div className="flex shrink-0 items-center gap-1" data-testid="view-controls">
      {captions && (
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          View
        </span>
      )}
      <button
        type="button"
        data-testid="home-view-btn"
        onClick={onFlyHome}
        title="Fly to home view (H)"
        aria-label="Fly to home view"
        className="flex h-7 w-7 items-center justify-center rounded border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
      >
        {/* house glyph */}
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 11.5 12 4l9 7.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5 10v9h5v-5h4v5h5v-9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        data-testid="set-home-view-btn"
        onClick={onSetHome}
        title="Set the current view as my home view"
        aria-label="Set current view as home"
        className={`flex h-7 items-center gap-1 rounded border px-1.5 text-[11px] ${
          homeJustSet
            ? 'border-emerald-600 bg-emerald-800/60 text-emerald-200'
            : 'border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700'
        }`}
      >
        {homeJustSet ? (
          <>
            <span aria-hidden>✓</span>
            {captions && <span>Saved</span>}
          </>
        ) : (
          <>
            {/* pin/target glyph */}
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 12v.01M12 2v3M12 19v3M2 12h3M19 12h3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {captions && <span>Set home</span>}
          </>
        )}
      </button>
      {/* National view (Phase 6) — ALWAYS frames all of Canada, ignoring any saved
          home (the deliberate counterpart to Home). Glyph = a fit-to-extent corner-
          brackets mark ("frame the whole country"): a clean, instantly-legible frame
          that reads distinctly from the house (= my saved spot) and the globe
          (= Regions). [FLAG-HUMAN] this is the documented FALLBACK glyph — the IMS-pick
          maple leaf was dropped because §G review + render-verification showed every
          hand-rolled leaf path renders as an unrecognizable starburst at this size; a
          vetted maple-leaf asset can be swapped back here if the founder prefers it.
          Square h-7 w-7 icon button at narrow tiers; expands with a "National" caption
          at wide tiers (like Set home). ⇧H mirrors it. */}
      <button
        type="button"
        data-testid="national-view-btn"
        onClick={onFlyNational}
        title="National view — all of Canada (⇧H)"
        aria-label="National view"
        className={`flex h-7 items-center justify-center gap-1 rounded border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 ${
          captions ? 'px-1.5 text-[11px]' : 'w-7'
        }`}
      >
        {/* fit-to-extent corner-brackets glyph (stroked — pairs with the stroked house = home) */}
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M4 8V5a1 1 0 0 1 1-1h3" />
          <path d="M16 4h3a1 1 0 0 1 1 1v3" />
          <path d="M20 16v3a1 1 0 0 1-1 1h-3" />
          <path d="M8 20H5a1 1 0 0 1-1-1v-3" />
        </svg>
        {captions && <span>National</span>}
      </button>
      <RegionsMenu
        savedRegions={savedRegions}
        onFlyRegion={onFlyRegion}
        onSaveRegion={onSaveRegion}
        onDeleteRegion={onDeleteRegion}
        canSaveRegion={canSaveRegion}
      />
    </div>
  );
}

/**
 * VIEW → Regions ▾ (#154). A popover listing the operator's saved region bboxes
 * (click to fly), each with a delete affordance, plus a name-it-and-save row that
 * captures the current map extent. Mirrors the AUTHOR menu's outside-click/Escape
 * dismissal.
 */
function RegionsMenu({
  savedRegions,
  onFlyRegion,
  onSaveRegion,
  onDeleteRegion,
  canSaveRegion,
  variant = 'bar',
}: {
  savedRegions: SavedRegion[];
  onFlyRegion: (region: SavedRegion) => void;
  onSaveRegion: (label: string) => void;
  onDeleteRegion: (region: SavedRegion) => void;
  canSaveRegion: boolean;
  /** 'bar' = command-bar trigger button; 'row' = a Settings ▾ menu row whose
   *  popover opens to the LEFT of the settings menu (which hugs the screen's
   *  right edge). */
  variant?: 'bar' | 'row';
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const captions = showBarCaptions(useCommandBarTier());

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const submitSave = (): void => {
    const trimmed = label.trim();
    if (!trimmed || !canSaveRegion) return;
    onSaveRegion(trimmed);
    setLabel('');
  };

  return (
    <div
      ref={wrapRef}
      className={variant === 'row' ? 'relative' : 'relative flex shrink-0 items-center'}
      data-testid="regions-controls"
    >
      {variant === 'row' ? (
        <button
          type="button"
          data-testid="regions-menu-btn"
          onClick={() => setOpen((v) => !v)}
          title="Saved regions — fly to a saved region or save the current view"
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
        >
          <span>Regions</span>
          <span className="text-slate-500" aria-hidden>
            {savedRegions.length > 0 ? `${savedRegions.length} ◂` : '◂'}
          </span>
        </button>
      ) : (
        <button
          type="button"
          data-testid="regions-menu-btn"
          onClick={() => setOpen((v) => !v)}
          title="Saved regions — fly to a saved region or save the current view"
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex h-7 items-center gap-1 rounded border border-slate-600 bg-slate-800 px-1.5 text-[11px] text-slate-300 hover:bg-slate-700"
        >
          {captions ? (
            <span>Regions</span>
          ) : (
            <span aria-hidden>
              {/* globe/region glyph for the compact bar */}
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
          <span aria-hidden>▾</span>
          {savedRegions.length > 0 && (
            <span className="rounded bg-slate-700 px-1 text-[10px] text-slate-300">
              {savedRegions.length}
            </span>
          )}
        </button>
      )}
      {open && (
        <div
          data-testid="regions-menu"
          role="menu"
          className={`absolute z-50 w-64 rounded-lg border border-slate-700 bg-slate-900/95 p-1 shadow-lg ${
            variant === 'row' ? 'right-full top-0 mr-1' : 'left-0 top-full mt-1'
          }`}
        >
          {savedRegions.length === 0 ? (
            <p className="px-2 py-1.5 text-[11px] text-slate-500">No saved regions yet.</p>
          ) : (
            <ul className="max-h-56 space-y-0.5 overflow-y-auto">
              {savedRegions.map((region) => (
                <li key={region.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    data-testid="region-fly-btn"
                    onClick={() => {
                      setOpen(false);
                      onFlyRegion(region);
                    }}
                    className="flex-1 truncate rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
                    title={`Fly to ${region.label}`}
                  >
                    {region.label}
                  </button>
                  <button
                    type="button"
                    data-testid="region-delete-btn"
                    onClick={() => onDeleteRegion(region)}
                    aria-label={`Delete region ${region.label}`}
                    title={`Delete ${region.label}`}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-800 hover:text-rose-300"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 border-t border-slate-700 pt-1">
            <div className="flex items-center gap-1 px-1 pb-1">
              <input
                data-testid="region-name-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSave();
                }}
                maxLength={80}
                placeholder="Name this view…"
                className="min-w-0 flex-1 rounded border border-slate-600 bg-slate-800 px-1.5 py-1 text-xs text-slate-100 placeholder:text-slate-500"
              />
              <button
                type="button"
                data-testid="region-save-btn"
                onClick={submitSave}
                disabled={!label.trim() || !canSaveRegion}
                title={
                  canSaveRegion
                    ? 'Save the current map extent as a named region'
                    : 'Pan or zoom the map once, then save'
                }
                className="shrink-0 rounded border border-emerald-500 bg-emerald-600/90 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * AUTHOR — a single Draw▾ split-button. When a map tool is running it becomes a
 * Cancel control instead (one button, never two competing affordances).
 */
function AuthorGroup({
  incidentSelected,
  toolActive,
  drawingSector,
  onDrawSector,
  onDeclareCommunity,
}: {
  incidentSelected: boolean;
  toolActive: boolean;
  drawingSector: boolean;
  onDrawSector: () => void;
  onDeclareCommunity: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tier = useCommandBarTier();
  const captions = showBarCaptions(tier);

  // Close the menu on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A tool is running → the button cancels it (calls the matching toggle).
  // Below the compact tier the label sheds the tool name (the title keeps it)
  // so the active-tool swell can't push the bar past its region.
  if (toolActive) {
    return (
      <div className="relative flex shrink-0 items-center gap-2 whitespace-nowrap" data-testid="author-controls">
        {captions && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Author
          </span>
        )}
        <button
          type="button"
          data-testid="author-cancel-btn"
          onClick={drawingSector ? onDrawSector : onDeclareCommunity}
          title={`Cancel ${drawingSector ? 'sector drawing' : 'community placement'}`}
          className="rounded border border-rose-500 bg-rose-700/90 px-3 py-1 text-xs font-medium text-white hover:bg-rose-700"
        >
          {compactCancelLabel(tier)
            ? '✕ Cancel (Esc)'
            : `✕ Cancel ${drawingSector ? 'Sector' : 'Community'} (Esc)`}
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative flex shrink-0 items-center gap-2 whitespace-nowrap" data-testid="author-controls">
      {captions && (
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Author
        </span>
      )}
      <button
        type="button"
        data-testid="author-draw-btn"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-slate-600 bg-slate-800 px-3 py-1 text-xs font-medium text-slate-200 hover:bg-slate-700"
      >
        ✏️ Draw ▾
      </button>
      {open && (
        <div
          data-testid="author-menu"
          className="absolute left-0 top-full z-50 mt-1 w-52 rounded-lg border border-slate-700 bg-slate-900/95 p-1 shadow-lg"
        >
          <button
            type="button"
            data-testid="draw-sector-btn"
            disabled={!incidentSelected}
            title={
              incidentSelected
                ? 'Divide the selected incident into geographic sectors (IMS §5.3)'
                : 'Select an incident first to draw its sectors'
            }
            onClick={() => {
              setOpen(false);
              onDrawSector();
            }}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-amber-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:text-slate-600 disabled:hover:bg-transparent"
          >
            <span aria-hidden>✏️</span>
            Draw Sector
          </button>
          <button
            type="button"
            data-testid="declare-community-btn"
            onClick={() => {
              setOpen(false);
              onDeclareCommunity();
            }}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-sky-200 hover:bg-slate-800"
          >
            <span aria-hidden>🏠</span>
            Add Community
          </button>
        </div>
      )}
    </div>
  );
}
